/**
 * ============================================================
 * OXVANDO — Header Behavior
 * assets/header.js
 * Vanilla JS, no dependencies.
 * Supports multiple header instances, Theme Editor
 * section reload/unload, and prefers-reduced-motion.
 * ============================================================
 */

(function () {
  'use strict';

  var ANNOUNCEMENT_INTERVAL = 5000;
  var FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function initSearch(header, cleanupFns) {
    var searchToggle = header.querySelector('[data-search-toggle]');
    var searchPanel = header.querySelector('[data-search-panel]');
    if (!searchToggle || !searchPanel) return;

    var closeBtn = searchPanel.querySelector('[data-search-close]');

    function openPanel() {
      searchPanel.hidden = false;
      searchToggle.setAttribute('aria-expanded', 'true');
      var input = searchPanel.querySelector('input[name="q"]');
      if (input) input.focus();
    }

    function closePanel() {
      searchPanel.hidden = true;
      searchToggle.setAttribute('aria-expanded', 'false');
      searchToggle.focus();
    }

    function onToggleClick() {
      if (searchPanel.hidden) {
        openPanel();
      } else {
        closePanel();
      }
    }

    function onPanelKeydown(event) {
      if (event.key === 'Escape') closePanel();
    }

    searchToggle.addEventListener('click', onToggleClick);
    searchPanel.addEventListener('keydown', onPanelKeydown);
    if (closeBtn) closeBtn.addEventListener('click', closePanel);

    cleanupFns.push(function () {
      searchToggle.removeEventListener('click', onToggleClick);
      searchPanel.removeEventListener('keydown', onPanelKeydown);
      if (closeBtn) closeBtn.removeEventListener('click', closePanel);
    });
  }

  function initMobileNav(header, cleanupFns) {
    var toggle = header.querySelector('[data-menu-toggle]');
    var nav = header.querySelector('[data-mobile-nav]');
    if (!toggle || !nav) return;

    var closeButton = nav.querySelector('[data-mobile-nav-close]');
    var lastFocused = null;
    var desktopMedia = window.matchMedia('(min-width: 990px)');

    function getFocusable() {
      return Array.prototype.slice.call(nav.querySelectorAll(FOCUSABLE_SELECTOR));
    }

    function onFocusTrap(event) {
      if (event.key !== 'Tab') return;
      var focusable = getFocusable();
      if (focusable.length === 0) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    function openNav() {
      lastFocused = document.activeElement;
      nav.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('ox-menu-open');

      var focusable = getFocusable();
      if (focusable.length) focusable[0].focus();

      nav.addEventListener('keydown', onFocusTrap);
    }

    function closeNav(restoreFocus) {
      nav.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('ox-menu-open');
      nav.removeEventListener('keydown', onFocusTrap);

      if (restoreFocus !== false) {
        (lastFocused || toggle).focus();
      }
    }

    function onToggle() {
      if (nav.hidden) {
        openNav();
      } else {
        closeNav(true);
      }
    }

    function onDocKeydown(event) {
      if (event.key === 'Escape' && !nav.hidden) closeNav(true);
    }

    function onCloseClick() {
      closeNav(true);
    }

    // If the viewport crosses into desktop while the drawer is open
    // (e.g. rotating a tablet, or resizing a browser window), close it
    // without stealing focus or leaving the page scroll-locked — but do
    // move focus somewhere visible/interactive for keyboard users, since
    // it could otherwise still sit on an element that's now hidden.
    function onBreakpointChange(event) {
      if (event.matches && !nav.hidden) {
        closeNav(false);
        var logo = header.querySelector('.ox-header__logo');
        if (logo) logo.focus();
      }
    }

    toggle.addEventListener('click', onToggle);
    document.addEventListener('keydown', onDocKeydown);
    if (closeButton) closeButton.addEventListener('click', onCloseClick);
    desktopMedia.addEventListener('change', onBreakpointChange);

    cleanupFns.push(function () {
      toggle.removeEventListener('click', onToggle);
      document.removeEventListener('keydown', onDocKeydown);
      nav.removeEventListener('keydown', onFocusTrap);
      desktopMedia.removeEventListener('change', onBreakpointChange);
      if (closeButton) closeButton.removeEventListener('click', onCloseClick);
      document.body.classList.remove('ox-menu-open');
    });
  }

  function initCart(header, cleanupFns) {
    var cartToggle = header.querySelector('[data-cart-toggle]');
    var countElement = header.querySelector('[data-cart-count]');
    var labelBase = cartToggle ? cartToggle.getAttribute('data-cart-label-base') : null;

    function onCartUpdated(event) {
      if (!event.detail) return;
      var count = Number(event.detail.item_count || 0);

      if (countElement) {
        countElement.textContent = String(count);
        countElement.hidden = count === 0;
      }
      if (cartToggle && labelBase) {
        cartToggle.setAttribute('aria-label', labelBase + ' (' + count + ')');
      }
    }

    document.addEventListener('ox:cart-updated', onCartUpdated);
    cleanupFns.push(function () {
      document.removeEventListener('ox:cart-updated', onCartUpdated);
    });
  }

  function initAnnouncementRotation(header, cleanupFns) {
    var track = header.querySelector('[data-announcement-track]');
    if (!track) return;

    var items = Array.prototype.slice.call(track.querySelectorAll('.ox-announcement__item'));
    if (items.length <= 1) return;

    var control = header.querySelector('[data-announcement-control]');
    var controlLabel = control ? control.querySelector('[data-announcement-control-label]') : null;
    var iconPause = control ? control.querySelector('[data-announcement-icon-pause]') : null;
    var iconPlay = control ? control.querySelector('[data-announcement-icon-play]') : null;

    var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var current = 0;
    var intervalId = null;
    // Manual pause takes priority over hover/focus auto-pause: once the
    // person clicks pause, hovering away must not silently restart it.
    var manuallyPaused = prefersReducedMotion;

    function setControlState(paused) {
      if (!control) return;
      control.setAttribute('aria-pressed', String(paused));
      if (controlLabel) {
        controlLabel.textContent = paused
          ? (control.getAttribute('data-play-text') || 'Play announcements')
          : (control.getAttribute('data-pause-text') || 'Pause announcements');
      }
      if (iconPause) iconPause.hidden = paused;
      if (iconPlay) iconPlay.hidden = !paused;
    }

    function startRotation() {
      if (intervalId || manuallyPaused) return;
      intervalId = window.setInterval(function () {
        items[current].removeAttribute('data-active');
        current = (current + 1) % items.length;
        items[current].setAttribute('data-active', '');
      }, ANNOUNCEMENT_INTERVAL);
    }

    function stopRotation() {
      window.clearInterval(intervalId);
      intervalId = null;
    }

    function onMouseEnter() { stopRotation(); }
    function onMouseLeave() { startRotation(); }
    function onFocusIn() { stopRotation(); }
    function onFocusOut() { startRotation(); }

    function onControlClick() {
      manuallyPaused = !manuallyPaused;
      setControlState(manuallyPaused);
      if (manuallyPaused) {
        stopRotation();
      } else {
        startRotation();
      }
    }

    track.addEventListener('mouseenter', onMouseEnter);
    track.addEventListener('mouseleave', onMouseLeave);
    track.addEventListener('focusin', onFocusIn);
    track.addEventListener('focusout', onFocusOut);
    if (control) control.addEventListener('click', onControlClick);

    setControlState(manuallyPaused);
    if (!manuallyPaused) startRotation();

    cleanupFns.push(function () {
      stopRotation();
      track.removeEventListener('mouseenter', onMouseEnter);
      track.removeEventListener('mouseleave', onMouseLeave);
      track.removeEventListener('focusin', onFocusIn);
      track.removeEventListener('focusout', onFocusOut);
      if (control) control.removeEventListener('click', onControlClick);
    });
  }

  function initStickyShadow(header, cleanupFns) {
    if (header.dataset.sticky !== 'true') return;

    var ticking = false;

    function onScroll() {
      header.classList.toggle('ox-header--scrolled', window.scrollY > 4);
      ticking = false;
    }

    function onScrollRaf() {
      if (!ticking) {
        window.requestAnimationFrame(onScroll);
        ticking = true;
      }
    }

    window.addEventListener('scroll', onScrollRaf, { passive: true });
    cleanupFns.push(function () {
      window.removeEventListener('scroll', onScrollRaf);
    });
  }

  function initHeader(header) {
    if (!header || header.dataset.initialized === 'true') return;
    header.dataset.initialized = 'true';

    var cleanupFns = [];
    header._oxCleanup = cleanupFns;

    initSearch(header, cleanupFns);
    initMobileNav(header, cleanupFns);
    initCart(header, cleanupFns);
    initAnnouncementRotation(header, cleanupFns);
    initStickyShadow(header, cleanupFns);
  }

  function teardownHeader(header) {
    if (header && Array.isArray(header._oxCleanup)) {
      header._oxCleanup.forEach(function (fn) { fn(); });
      header._oxCleanup = null;
    }
    if (header) header.dataset.initialized = 'false';
  }

  function initAllHeaders(root) {
    root.querySelectorAll('[data-header]').forEach(initHeader);
  }

  initAllHeaders(document);

  // Guard against attaching these document-level listeners more than once
  // across repeated script evaluations in a long Theme Editor session.
  if (!window.__oxHeaderListenersBound) {
    window.__oxHeaderListenersBound = true;

    document.addEventListener('shopify:section:load', function (event) {
      initAllHeaders(event.target);
    });

    document.addEventListener('shopify:section:unload', function (event) {
      var header = event.target.querySelector
        ? event.target.querySelector('[data-header]')
        : null;
      teardownHeader(header);
    });
  }
})();
